import { useState } from "react";
import { usePagination } from "@/hooks/usePagination";
import { ArrowDownCircle, Pencil, Search, Target, Trash2, X, Lock } from "lucide-react";
import PaginationControls from "../PaginationControls";
import { api, formatApiError, formatIDR } from "@/lib/api";
import { MonthNav } from "./MonthNav";
import { toast } from "sonner";

/* ================================================================
   ============= TAB: JURNAL AKUNTANSI (Debet/Kredit) =============
   - KREDIT = semua pemasukan selain akun 101
   - DEBET  = semua pengeluaran
   - Saldo berjalan = Kredit − Debet (Saldo awal setiap bulan 0)
   Data sumber sama dengan Buku Kas — hanya berbeda di akun pemasukan.
   ================================================================ */
export function JournalTab({ month, setMonth, monthLabel, prevMonthLabel, shiftMonth, search, setSearch, txData, filtered, loading, onEdit, onRemove, onAdjustBalance, currentBalance, kasbonOpen, totalPiutang = 0, onChanged, todayISO }) {
  const [openBayarPiutang, setOpenBayarPiutang] = useState(false);
  // === RUMUS MATEMATIKA MURNI (Jurnal Akuntansi) ===
  // Setiap bulan mulai bersih dari 0, hanya akumulasi transaksi bulan berjalan.
  const openingBalance = 0;

  // Total Pemasukan = SEMUA `in` (kecuali 101)
  const totalPemasukan = filtered.reduce(
    (s, t) => s + (t.type === "in" ? Number(t.amount || 0) : 0),
    0
  );
  // Total Pengeluaran = SEMUA `out`
  const totalPengeluaran = filtered.reduce(
    (s, t) => s + (t.type === "out" ? Number(t.amount || 0) : 0),
    0
  );
  const saldoAkhirComputed = openingBalance + totalPemasukan - totalPengeluaran;

  // Running balance per baris: sequential, ADD semua in, SUBTRACT semua out
  const balanceByRowIndex = (() => {
    let running = openingBalance;
    return filtered.map((t) => {
      if (t.type === "in") running += Number(t.amount || 0);
      else if (t.type === "out") running -= Number(t.amount || 0);
      return running;
    });
  })();
  
  const {
    page,
    pageSize,
    totalItems,
    totalPages,
    startIndex,
    paginatedItems,
    goToPage,
  } = usePagination(filtered, { resetKey: `${month}:${search}` });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <MonthNav value={month} onChange={setMonth} testIdPrefix="cash-month" prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth} />
          <div className="relative flex-1 md:flex-none">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari keterangan / akun / ref…"
              data-testid="cash-search"
              className="rounded-none border border-zinc-300 bg-white pl-10 pr-3 py-2 text-sm w-full md:w-72 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto justify-between md:justify-end">
          <div className="text-xs text-zinc-500 font-mono">
            {filtered.length} transaksi
          </div>
          <button
            type="button"
            data-testid="cash-adjust-balance-btn"
            onClick={onAdjustBalance}
            className="rounded-none border border-[#002FA7] bg-white text-[#002FA7] px-3 py-2 text-[11px] md:text-xs font-bold uppercase tracking-wider hover:bg-[#002FA7] hover:text-white inline-flex items-center gap-2"
            title="Bikin jurnal penyesuaian otomatis agar saldo kas real-time menjadi angka target."
          >
            <Target className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Update Saldo Kas Terakhir</span>
            <span className="sm:hidden">Update Saldo</span>
          </button>
        </div>
      </div>

      {/* Ringkasan Piutang Perusahaan (khusus Jurnal Akuntansi) */}
      <div data-testid="jurnal-piutang-card" className="mb-4 border border-[#002FA7]/20 bg-[#002FA7]/[0.03] px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#002FA7]/80 font-bold">Total Piutang Perusahaan (Akun 102-PTP)</div>
          <div className="text-[10px] font-mono text-zinc-500 mt-0.5">Akumulasi net (in − out) transaksi bulan {monthLabel(month)}. Khusus Jurnal Akuntansi.</div>
        </div>
        <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4">
          <div data-testid="jurnal-piutang-value" className={`font-mono text-xl md:text-2xl font-bold ${totalPiutang >= 0 ? "text-[#002FA7]" : "text-[#E81123]"}`}>
            {formatIDR(totalPiutang)}
          </div>
          <button
            type="button"
            data-testid="btn-bayar-piutang"
            onClick={() => setOpenBayarPiutang(true)}
            className="rounded-none bg-[#002FA7] text-white px-3 md:px-4 py-2 text-[11px] md:text-xs font-bold uppercase tracking-wider hover:bg-[#001F7A] inline-flex items-center gap-2 whitespace-nowrap"
            title="Catat pelunasan piutang perusahaan. Insert 1 baris transaksi type=out ke akun 102-PTP. Tidak menyentuh Buku Kas."
          >
            <ArrowDownCircle className="w-3.5 h-3.5" /> Bayar Piutang
          </button>
        </div>
      </div>

      {openBayarPiutang && (
        <PiutangBayarModal
          onClose={() => setOpenBayarPiutang(false)}
          onSaved={async () => { setOpenBayarPiutang(false); if (onChanged) await onChanged(); }}
          todayISO={todayISO}
        />
      )}

      <div className="hidden md:block border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-left text-sm table-fixed">
          <colgroup>
            <col className="w-[110px]" />
            <col className="w-[90px]" />
            <col className="w-[180px]" />
            <col />
            <col className="w-[130px]" />
            <col className="w-[130px]" />
            <col className="w-[140px]" />
            <col className="w-[90px]" />
          </colgroup>
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200 text-[11px] font-bold text-zinc-600 uppercase tracking-widest">
              <th className="px-4 py-3">Tanggal</th>
              <th className="px-4 py-3">Kode Akun</th>
              <th className="px-4 py-3">Nama Akun</th>
              <th className="px-4 py-3">Keterangan</th>
              <th className="px-4 py-3 text-right">Pemasukan</th>
              <th className="px-4 py-3 text-right">Pengeluaran</th>
              <th className="px-4 py-3 text-right bg-[#002FA7]/5 text-[#002FA7]">Saldo Kas</th>
              <th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {/* Baris SALDO AWAL — selalu di atas sebagai titik awal perhitungan */}
            <tr data-testid="journal-saldo-awal-row" className="bg-[#002FA7]/5 border-b-2 border-[#002FA7]/30">
              <td colSpan={6} className="px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-widest text-[#002FA7]">
                  Saldo Awal {monthLabel(month)}
                </span>
                <span className="ml-2 text-[10px] text-zinc-500 font-mono">(pindahan bulan sebelumnya)</span>
              </td>
              <td className="px-4 py-3 text-right font-mono text-sm font-bold text-[#002FA7] bg-[#002FA7]/10 whitespace-nowrap" data-testid="journal-saldo-awal-value">
                {formatIDR(openingBalance)}
              </td>
              <td className="px-4 py-3"></td>
            </tr>
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-400 font-mono text-xs">Memuat…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-400 font-mono text-xs">Belum ada transaksi non-Kas bulan ini.</td></tr>
            )}
            {paginatedItems.map((t, pageIndex) => (
              <tr key={t.id} data-testid="cash-tx-row" className={`border-b border-zinc-100 hover:bg-zinc-50/80 ${t.auto ? "bg-amber-50/30" : ""}`}>
                <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">
                  {t.date}
                  {t.auto && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-amber-700 inline-flex items-center gap-1 align-middle" title="Transaksi otomatis dari modul sumbernya"><Lock className="w-2.5 h-2.5" /></span>
                  )}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs font-bold text-zinc-700 whitespace-nowrap">{t.account_code}</td>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-medium truncate" title={t.account_name}>{t.account_name}</div>
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <div className="break-words">{t.description}</div>
                  {t.reference && <div className="text-[10px] font-mono text-zinc-400 mt-0.5">ref: {t.reference}</div>}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">{t.type === "in" ? <span className="text-[#008A00] font-bold">{formatIDR(t.amount)}</span> : ""}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">{t.type === "out" ? <span className="text-[#E81123] font-bold">{formatIDR(t.amount)}</span> : ""}</td>
                <td data-testid="journal-saldo-cell" className="px-4 py-2.5 text-right font-mono text-xs font-bold text-[#002FA7] bg-[#002FA7]/5 whitespace-nowrap">{formatIDR(balanceByRowIndex[startIndex + pageIndex])}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    {t._virtual ? (
                      <span className="text-[10px] text-zinc-400 font-mono px-2" title="Cash Plaza — virtual dari koleksi Sales. Edit/hapus lewat modul Penjualan.">auto</span>
                    ) : (
                      <>
                        <button data-testid="edit-tx-button" onClick={() => onEdit(t)} disabled={t.auto} className="p-1.5 hover:bg-zinc-100 text-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed" title={t.auto ? "Transaksi otomatis — edit di modul sumbernya" : "Edit"}><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid="del-tx-button" onClick={() => onRemove(t)} className="p-1.5 hover:bg-[#E81123]/10 text-[#E81123]" title={t.auto ? "Coba hapus (sistem akan cek orphan)" : "Hapus"}><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && filtered.length > 0 && (
              <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                <td className="px-4 py-3" colSpan={4}>
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-900">Saldo Akhir {monthLabel(month)}</span>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                    Rumus: {formatIDR(openingBalance)} (Saldo Awal) + {formatIDR(totalPemasukan)} (Pemasukan) − {formatIDR(totalPengeluaran)} (Pengeluaran) = {formatIDR(saldoAkhirComputed)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono font-bold text-[#008A00]">{formatIDR(totalPemasukan)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-[#E81123]">{formatIDR(totalPengeluaran)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-[#002FA7] bg-[#002FA7]/5 text-lg" data-testid="journal-saldo-total">{formatIDR(saldoAkhirComputed)}</td>
                <td className="px-4 py-3"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2">
        <div className="bg-[#002FA7]/5 border border-[#002FA7]/30 px-3 py-2.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">Saldo Awal {monthLabel(month)}</div>
              <div className="text-[10px] text-zinc-500 font-mono">pindahan bulan sebelumnya</div>
            </div>
            <div className="font-mono font-bold text-sm text-[#002FA7]">{formatIDR(openingBalance)}</div>
          </div>
        </div>

        {loading && (
          <div className="text-center text-zinc-400 text-xs font-mono py-8">Memuat…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-zinc-400 text-xs font-mono py-8 border border-dashed border-zinc-200">
            Belum ada transaksi non-Kas bulan ini.
          </div>
        )}
        {!loading && paginatedItems.map((t, pageIndex) => (
          <div key={t.id} data-testid="mobile-tx-card" className={`border border-zinc-200 bg-white p-3 ${t._virtual ? "border-l-4 border-l-[#008A00]" : ""}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">{t.date}</span>
                <span className="font-mono text-[11px] font-bold text-zinc-800 bg-zinc-100 px-1.5 py-0.5">{t.account_code}</span>
              </div>
              {t._virtual ? (
                <span className="text-[9px] text-zinc-400 font-mono">auto</span>
              ) : (
                <div className="flex items-center gap-0.5">
                  <button onClick={() => onEdit(t)} disabled={t.auto} className="p-1 hover:bg-zinc-100 text-zinc-700 disabled:opacity-30" data-testid="edit-tx-button-mobile"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onRemove(t)} className="p-1 hover:bg-[#E81123]/10 text-[#E81123]" data-testid="del-tx-button-mobile"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
            <div className="text-sm font-semibold text-zinc-900">{t.account_name}</div>
            <div className="text-xs text-zinc-600 mt-0.5 break-words">{t.description}</div>
            {t.reference && (
              <div className="text-[10px] font-mono text-zinc-400 mt-0.5">ref: {t.reference}</div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-zinc-100 text-[11px] font-mono">
              <div>
                <div className="text-[9px] uppercase text-zinc-400">In</div>
                <div className="text-[#008A00] font-bold">{t.type === "in" ? formatIDR(t.amount) : "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-400">Out</div>
                <div className="text-[#E81123] font-bold">{t.type === "out" ? formatIDR(t.amount) : "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-400">Saldo</div>
                <div className="text-[#002FA7] font-bold">{formatIDR(balanceByRowIndex[startIndex + pageIndex])}</div>
              </div>
            </div>
          </div>
        ))}

        {!loading && filtered.length > 0 && (
          <div className="bg-zinc-900 text-white p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold">Saldo Akhir {monthLabel(month)}</div>
            <div className="text-[9px] font-mono text-zinc-400 mt-0.5 mb-2">
              {formatIDR(openingBalance)} + {formatIDR(totalPemasukan)} − {formatIDR(totalPengeluaran)}
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
              <div>
                <div className="text-[9px] uppercase text-zinc-400">In</div>
                <div className="text-[#00c800] font-bold">{formatIDR(totalPemasukan)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-400">Out</div>
                <div className="text-[#ff6b6b] font-bold">{formatIDR(totalPengeluaran)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-400">Total</div>
                <div className="text-white font-bold text-sm">{formatIDR(saldoAkhirComputed)}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {!loading && (
        <PaginationControls
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={goToPage}
          testIdPrefix="cashbook-journal-pagination"
        />
      )}
    </div>
  );
}

/* ---------- Piutang Bayar Modal (KHUSUS Jurnal Akuntansi — akun 102-PTP) ---------- */
/* SATU-satunya efek: POST /cashbook/piutang/bayar → insert 1 baris tx type=out di akun 102-PTP.
   TIDAK menyentuh logic Buku Kas. Filter Buku Kas (`filteredBook`) sudah exclude 102-PTP.  */
function PiutangBayarModal({ onClose, onSaved, todayISO }) {
  const [form, setForm] = useState({
    date: todayISO(),
    amount: 0,
    description: "",
    reference: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.amount) <= 0) { toast.error("Jumlah pelunasan harus > 0"); return; }
    setSaving(true);
    try {
      await api.post("/cashbook/piutang/bayar", {
        date: form.date,
        amount: Number(form.amount),
        description: form.description.trim() || "Pelunasan Piutang Perusahaan",
        reference: form.reference.trim() || null,
      });
      toast.success("Pelunasan piutang tercatat");
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan pelunasan");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-300 w-full max-w-lg" data-testid="piutang-bayar-modal">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#002FA7]">
              <ArrowDownCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Jurnal Akuntansi</div>
              <div className="font-heading text-xl font-bold text-zinc-900">Bayar Piutang Perusahaan</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-piutang-bayar-modal" className="p-1.5 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="text-[11px] font-mono text-zinc-500 bg-zinc-50 border border-zinc-200 p-2.5">
            <b className="text-zinc-900">102-PTP · Piutang Perusahaan</b><br/>
            Aksi ini insert 1 baris transaksi <b>type=out</b> ke akun ini. Menurunkan total piutang di Jurnal Akuntansi. Tidak muncul di Buku Kas.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Tanggal</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="piutang-bayar-date"
                className="w-full rounded-none border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Jumlah (Rp)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="piutang-bayar-amount"
                className="w-full rounded-none border border-zinc-300 bg-white px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Keterangan</label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Pelunasan Piutang Perusahaan"
              data-testid="piutang-bayar-desc"
              className="w-full rounded-none border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Referensi (opsional)</label>
            <input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="No. nota, no. bukti, dsb."
              data-testid="piutang-bayar-ref"
              className="w-full rounded-none border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-none border border-zinc-300 bg-white text-zinc-700 px-4 py-2 text-sm hover:bg-zinc-50" data-testid="piutang-bayar-cancel">Batal</button>
            <button type="submit" disabled={saving} className="rounded-none bg-[#002FA7] text-white px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#001F7A] disabled:opacity-50" data-testid="piutang-bayar-submit">
              {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}